import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import ImageSourcePickerModal from '../../components/common/ImageSourcePickerModal/ImageSourcePickerModal';
import Loading from '../../components/common/Loading/Loading';
import Logo from '../../components/common/Logo/Logo';
import {
  PHOTO_GUIDELINES_COPY,
  PhotoGuidelines,
} from '../../components/photoGuidelines';
import type { PhotoGuidelineSlot } from '../../components/photoGuidelines';
import {
  getMissingGuidelinePhotoSlots,
  getPhotoGuidelineSectionId,
} from '../../components/photoGuidelines';
import { useBodyPhotoUpload } from '../../hooks/useBodyPhotoUpload';
import {
  PublicStudentContextService,
  PublicStudentTokenAlreadyUsedError,
  StudentPhotoUploadService,
} from '../../services';
import { canOfferCameraCapture } from '../../utils/canOfferCameraCapture';
import { hasCompleteBodyPhotosForCurrentPeriod } from '../../utils/studentBodyPhotoPeriod';
import { PUBLIC_LINK_GENERIC_ERROR } from '../../utils/publicLinkErrors';
import { STUDENT_TOKEN_QUERY_PARAM } from '../../utils/studentPublicLinks';
import { getOnboardingRoutePaths } from '../../config/hostApps';
import fupStyles from '../Fup/Fup.module.css';
import styles from './PhotoGuidelinesPage.module.css';

const formatGreetingName = (fullName: string): string => {
  const trimmed = fullName.trim();

  if (!trimmed) {
    return 'aluna';
  }

  const [firstName] = trimmed.split(/\s+/);
  const normalized = firstName.toLowerCase();

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const scrollToPageTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const PHOTO_SLOT_TITLES: Record<PhotoGuidelineSlot, string> = {
  front: PHOTO_GUIDELINES_COPY.positions.front.title,
  back: PHOTO_GUIDELINES_COPY.positions.back.title,
  rightSide: PHOTO_GUIDELINES_COPY.positions.right_side.title,
  leftSide: PHOTO_GUIDELINES_COPY.positions.left_side.title,
};

const scrollToPhotoSlot = (slot: PhotoGuidelineSlot) => {
  window.requestAnimationFrame(() => {
    document.getElementById(getPhotoGuidelineSectionId(slot))?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  });
};

const PhotosAlreadyReceivedView: React.FC<{ studentName: string | null }> = ({ studentName }) => {
  const firstName = studentName ? formatGreetingName(studentName) : null;

  return (
    <div className={styles.alreadyReceived}>
      <p className={styles.greetingLead}>
        {firstName ? `Olá, ${firstName}` : 'Olá'}
      </p>
      <p className={styles.alreadyReceivedTitle}>Já recebemos suas fotos</p>
      <p className={styles.alreadyReceivedSubtitle}>
        Obrigada por compartilhar. Está tudo certo por aqui.
      </p>
    </div>
  );
};

const PhotoGuidelinesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studentToken = searchParams.get(STUDENT_TOKEN_QUERY_PARAM)?.trim() || '';
  const [studentId, setStudentId] = useState<number | null>(null);
  const [studentName, setStudentName] = useState<string | null>(null);
  const [isResolvingStudent, setIsResolvingStudent] = useState(Boolean(studentToken));
  const [isCheckingPhotos, setIsCheckingPhotos] = useState(false);
  const [photosAlreadyReceived, setPhotosAlreadyReceived] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [pendingRemoveSlot, setPendingRemoveSlot] = useState<PhotoGuidelineSlot | null>(null);
  const [showConfirmSubmitModal, setShowConfirmSubmitModal] = useState(false);
  const [missingSlots, setMissingSlots] = useState<PhotoGuidelineSlot[]>([]);
  const [sourcePickerSlot, setSourcePickerSlot] = useState<PhotoGuidelineSlot | null>(null);

  const {
    photos,
    isUploading,
    fileInputRef,
    cameraInputRef,
    openFilePicker,
    addPhotoFromFile,
    handleFileChange,
    removePhoto,
    uploadPhotos,
  } = useBodyPhotoUpload({ studentId, studentToken });

  const handleSelectPhoto = (slot: PhotoGuidelineSlot) => {
    if (canOfferCameraCapture()) {
      setSourcePickerSlot(slot);
      return;
    }

    openFilePicker(slot, 'gallery');
  };

  useEffect(() => {
    setMissingSlots((current) => current.filter((slot) => !photos[slot]));
  }, [photos]);

  useEffect(() => {
    if (!studentToken) {
      setStudentId(null);
      setResolveError(PUBLIC_LINK_GENERIC_ERROR);
      setIsResolvingStudent(false);
      setIsCheckingPhotos(false);
      setPhotosAlreadyReceived(false);
      return;
    }

    let isMounted = true;

    const resolveStudent = async () => {
      setIsResolvingStudent(true);
      setIsCheckingPhotos(false);
      setPhotosAlreadyReceived(false);
      setResolveError(null);

      try {
        const context = await PublicStudentContextService.resolve(studentToken);

        if (!isMounted) {
          return;
        }

        setStudentId(context.student.id);
        setStudentName(context.student.full_name);
        setIsResolvingStudent(false);
        setIsCheckingPhotos(true);

        const assessments = await StudentPhotoUploadService.listBodyPhotoAssessments({
          studentId: context.student.id,
          studentToken,
        });

        if (!isMounted) {
          return;
        }

        const alreadyReceived = hasCompleteBodyPhotosForCurrentPeriod(
          assessments,
          new Date(),
          { cycleStartedOn: context.photoCycleStartedOn },
        );
        setPhotosAlreadyReceived(alreadyReceived);

        if (alreadyReceived) {
          await StudentPhotoUploadService.completePhotoUploadLink({
            studentId: context.student.id,
            studentToken,
          });
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error instanceof PublicStudentTokenAlreadyUsedError) {
          setStudentId(null);
          setStudentName(null);
          setPhotosAlreadyReceived(true);
          setResolveError(null);
          return;
        }

        setStudentId(null);
        setStudentName(null);
        setPhotosAlreadyReceived(false);
        setResolveError(
          error instanceof Error ? error.message : 'Não foi possível identificar a aluna para envio de fotos.',
        );
      } finally {
        if (isMounted) {
          setIsResolvingStudent(false);
          setIsCheckingPhotos(false);
        }
      }
    };

    void resolveStudent();

    return () => {
      isMounted = false;
    };
  }, [studentToken]);

  const handleOpenConfirmSubmit = () => {
    setFeedback(null);

    const pendingSlots = getMissingGuidelinePhotoSlots(photos);

    if (pendingSlots.length > 0) {
      setMissingSlots(pendingSlots);
      scrollToPhotoSlot(pendingSlots[0]);
      return;
    }

    setShowConfirmSubmitModal(true);
  };

  const handleSave = async () => {
    setFeedback(null);

    try {
      await uploadPhotos();
      setShowConfirmSubmitModal(false);
      setMissingSlots([]);
      setFeedback(null);
      setPhotosAlreadyReceived(true);
      scrollToPageTop();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ocorreu um erro. Por favor, tente novamente.';

      if (/já recebemos suas fotos/i.test(message)) {
        setShowConfirmSubmitModal(false);
        setPhotosAlreadyReceived(true);
        setFeedback(null);
        scrollToPageTop();
        return;
      }

      setFeedback({
        message,
        variant: 'error',
      });
      scrollToPageTop();
    }
  };

  const handleRemoveRequest = (slot: PhotoGuidelineSlot) => {
    if (!photos[slot]) {
      return;
    }

    setPendingRemoveSlot(slot);
  };

  const handleConfirmRemove = () => {
    if (!pendingRemoveSlot) {
      return;
    }

    removePhoto(pendingRemoveSlot);
    setPendingRemoveSlot(null);
  };

  if (isResolvingStudent || isCheckingPhotos) {
    return <Loading fullscreen />;
  }

  if (photosAlreadyReceived) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <header className={styles.header}>
            <Logo />
          </header>
          <PhotosAlreadyReceivedView studentName={studentName} />
        </div>
      </div>
    );
  }

  if (resolveError || !studentId) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <AlertBanner variant="error" message={resolveError ?? 'Aluna não identificada para envio de fotos.'} />
          <div className={styles.footer}>
            <Button
              type="button"
              variant="outline"
              size="small"
              onClick={() => navigate(getOnboardingRoutePaths().root)}
            >
              Voltar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <Logo />
        </header>

        {feedback && (
          <AlertBanner
            variant={feedback.variant}
            message={feedback.message}
            className={styles.feedbackTop}
          />
        )}

        {studentName && (
          <div className={styles.greeting}>
            <p className={styles.greetingLead}>Olá,</p>
            <p className={styles.greetingName}>{formatGreetingName(studentName)}</p>
            <p className={styles.greetingSubtitle}>
              Envie suas fotos de acompanhamento seguindo as orientações abaixo.
            </p>
          </div>
        )}

        <PhotoGuidelines
          photos={photos}
          missingSlots={missingSlots}
          onSelectPhoto={handleSelectPhoto}
          onRemovePhoto={handleRemoveRequest}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className={styles.hiddenInput}
          onChange={(event) => {
            void handleFileChange(event);
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className={styles.hiddenInput}
          onChange={(event) => {
            void handleFileChange(event);
          }}
        />

        <ImageSourcePickerModal
          open={sourcePickerSlot !== null}
          title="Adicionar foto corporal"
          onFilesSelected={(files) => {
            const slot = sourcePickerSlot;
            setSourcePickerSlot(null);
            if (slot && files[0]) {
              void addPhotoFromFile(slot, files[0]);
            }
          }}
          onCancel={() => setSourcePickerSlot(null)}
        />

        <footer className={`${fupStyles.footerActions} ${styles.submitFooter}`}>
          <Button
            type="button"
            variant="primary"
            size="app"
            fullWidth
            onClick={handleOpenConfirmSubmit}
            disabled={isUploading}
          >
            {isUploading ? 'Salvando...' : 'Enviar fotos'}
          </Button>
        </footer>
      </div>

      <ConfirmModal
        open={showConfirmSubmitModal}
        title="Confirmar envio"
        confirmLabel="Confirmar envio"
        cancelLabel="Cancelar"
        variant="warning"
        loading={isUploading}
        onCancel={() => setShowConfirmSubmitModal(false)}
        onConfirm={() => {
          void handleSave();
        }}
      >
        <p>Após enviar suas fotos, não será possível alterá-las posteriormente.</p>
        <p>Está tudo certo? Deseja realmente enviar suas fotos?</p>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingRemoveSlot)}
        title="Remover foto"
        message={
          pendingRemoveSlot
            ? `Deseja realmente remover a foto de ${PHOTO_SLOT_TITLES[pendingRemoveSlot].toLowerCase()}?`
            : 'Deseja realmente remover esta foto?'
        }
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="warning"
        icon="delete"
        onConfirm={handleConfirmRemove}
        onCancel={() => setPendingRemoveSlot(null)}
      />
    </div>
  );
};

export default PhotoGuidelinesPage;
